import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from '../apiClient';
import metadataService from '../services/MetadataService';

/* ─── Labourando dark theme tokens ─── */
const T = {
  bg: "#0a0f0c",
  surface: "#131a16",
  surfaceSoft: "#101512",
  surfaceWarm: "#17150f",
  surfaceMint: "#101813",
  surfaceBlue: "#10151d",
  accent: "#1daf5a",
  accentSoft: "hsla(145, 72%, 40%, 0.12)",
  accentBorder: "hsla(145, 72%, 40%, 0.3)",
  green: "#1daf5a",
  greenSoft: "hsla(145, 72%, 40%, 0.12)",
  greenBorder: "hsla(145, 72%, 40%, 0.28)",
  blue: "#eea62b",
  blueSoft: "hsla(38, 85%, 55%, 0.12)",
  blueBorder: "hsla(38, 85%, 55%, 0.24)",
  navy: "#ebebdf",
  text: "#ebebdf",
  textMed: "#838f8a",
  textLight: "#6f7b76",
  border: "#262e2a",
  borderWarm: "#3a3422",
  shadow: "0 20px 60px rgba(0,0,0,.35)",
};

const TICKET_INFO = {
  white_card: { label: "White Card (General Induction)", price: 120, days: 1, priority: 1, category: "foundation" },
  heights: { label: "Working at Heights", price: 350, days: 1, priority: 2, category: "core" },
  forklift: { label: "Forklift Licence", price: 450, days: 1, priority: 2, category: "core" },
  ewp: { label: "EWP / Boom Lift", price: 380, days: 1, priority: 3, category: "advanced" },
  confined_space: { label: "Confined Space", price: 400, days: 1, priority: 3, category: "advanced" },
  traffic_control: { label: "Traffic Control", price: 600, days: 2, priority: 3, category: "specialised" },
};

const STEPS = ["intro", "situation", "currentTickets", "interest", "challenge", "suburb", "contact", "result"];

export default function GetJobReadyQuiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [animKey, setAnimKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({
    situation: "",
    currentTickets: [],
    interest: "",
    challenge: [],
    suburb: "",
    firstName: "",
    phone: "",
    email: "",
  });

  const cur = STEPS[step];
  const pct = Math.round((step / (STEPS.length - 1)) * 100);

  const set = (k, v) => setAnswers(p => ({ ...p, [k]: v }));
  const toggleTicket = (t) => setAnswers(p => {
    if (t === "none") return { ...p, currentTickets: ["none"] };
    const f = p.currentTickets.filter(x => x !== "none");
    return { ...p, currentTickets: f.includes(t) ? f.filter(x => x !== t) : [...f, t] };
  });
  const toggleChallenge = (c) => setAnswers(p => {
    const cur = p.challenge;
    return { ...p, challenge: cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c] };
  });

  const go = (d) => { setDir(d); setAnimKey(k => k + 1); setStep(s => Math.max(0, Math.min(STEPS.length - 1, s + d))); };

  const canNext = () => {
    switch (cur) {
      case "situation": return !!answers.situation;
      case "currentTickets": return answers.currentTickets.length > 0;
      case "interest": return !!answers.interest;
      case "challenge": return answers.challenge.length > 0;
      case "suburb": return answers.suburb.trim().length > 0;
      case "contact": return answers.firstName.trim().length > 0 && answers.phone.trim().length >= 8;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await apiClient.post('/leads', { ...answers, source: 'quiz' });
      setResult(res);
      setDir(1);
      setAnimKey(k => k + 1);
      setStep(STEPS.length - 1);
    } catch (err) {
      console.error('Error submitting lead:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── pathway logic ─── */
  const buildPathway = () => {
    const has = new Set(answers.currentTickets);
    const hasNone = has.has("none");
    const needsWhiteCard = hasNone || !has.has("white_card");

    const foundation = [];
    if (needsWhiteCard) foundation.push("white_card");

    const core = [];
    const advanced = [];

    if (["construction", "general", "not_sure"].includes(answers.interest)) {
      if (!has.has("heights")) core.push("heights");
      if (!has.has("ewp")) advanced.push("ewp");
      if (!has.has("confined_space")) advanced.push("confined_space");
    }
    if (["warehouse", "general", "not_sure"].includes(answers.interest)) {
      if (!has.has("forklift")) core.push("forklift");
    }
    if (["traffic", "not_sure"].includes(answers.interest)) {
      if (!has.has("traffic_control")) advanced.push("traffic_control");
    }

    return { foundation, core, advanced, hasNone };
  };

  /* ─── shared UI pieces ─── */
  const Option = ({ selected, onClick, children, multi }) => (
    <div onClick={onClick} style={{
      padding: "16px 20px", borderRadius: 14, cursor: "pointer", transition: "all .2s",
      border: `1.5px solid ${selected ? T.accent : T.border}`,
      background: selected ? T.accentSoft : T.surface,
      display: "flex", alignItems: "center", gap: 14,
    }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = T.accentBorder; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = T.border; }}
    >
      <div style={{
        width: 22, height: 22, borderRadius: multi ? 6 : 22, flexShrink: 0,
        border: `2px solid ${selected ? T.accent : "#CBD5E1"}`,
        background: selected ? T.accent : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s",
      }}>
        {selected && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );

  const Btn = ({ primary, disabled, onClick, children }) => (
    <button disabled={disabled} onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 8, border: "none", cursor: disabled ? "not-allowed" : "pointer",
      padding: primary ? "14px 28px" : "12px 20px", borderRadius: 12, fontWeight: 700,
      fontSize: primary ? 16 : 14, fontFamily: "inherit", transition: "all .2s",
      background: primary ? `linear-gradient(135deg, ${T.accent}, #179c4b)` : T.surface,
      color: primary ? "#071b0e" : T.text,
      opacity: disabled ? 0.4 : 1,
      boxShadow: primary && !disabled ? "0 12px 30px rgba(29,165,90,.22)" : `inset 0 0 0 1px ${T.border}`,
    }}>{children}</button>
  );

  const SectionLabel = ({ emoji, title, sub }) => (
    <div style={{ marginBottom: 20 }}>
      <span style={{ fontSize: 28, display: "block", marginBottom: 10 }}>{emoji}</span>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: "0 0 6px", fontFamily: "'Outfit', sans-serif", lineHeight: 1.25 }}>{title}</h2>
      {sub && <p style={{ fontSize: 15, color: T.textMed, margin: 0, lineHeight: 1.5 }}>{sub}</p>}
    </div>
  );

  const inputStyle = {
    width: "100%", padding: "14px 18px", borderRadius: 12, fontSize: 16,
    border: `1.5px solid ${T.border}`, background: T.surface, color: T.text,
    outline: "none", fontFamily: "inherit", transition: "border-color .2s", boxSizing: "border-box",
  };
  const focusInput = (e) => e.target.style.borderColor = T.accent;
  const blurInput = (e) => { if (!e.target.value) e.target.style.borderColor = T.border; };

  /* ─── step renderers ─── */
  const renderStep = () => {
    switch (cur) {
      case "intro":
        return (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>👷</div>
            <h1 style={{
              fontSize: 30, fontWeight: 900, color: T.text, margin: "0 0 16px",
              fontFamily: "'Outfit', sans-serif", lineHeight: 1.2,
            }}>
              Struggling to get a job<br />in construction?
            </h1>
            <p style={{ fontSize: 17, color: T.textMed, maxWidth: 420, margin: "0 auto 12px", lineHeight: 1.6 }}>
              You're not alone. Breaking into the industry can feel overwhelming —
              figuring out what tickets you need, where to start, who's hiring.
            </p>
            <p style={{ fontSize: 17, color: T.text, fontWeight: 600, maxWidth: 420, margin: "0 auto 32px", lineHeight: 1.6 }}>
              Let us give you a hand. We'll build you a clear, step-by-step pathway to get working.
            </p>
            <Btn primary onClick={() => go(1)}>
              Let's figure this out together →
            </Btn>
            <p style={{ fontSize: 13, color: T.textLight, marginTop: 16 }}>Takes 2 minutes · completely free</p>
          </div>
        );

      case "situation":
        return (
          <div>
            <SectionLabel emoji="🤔" title="What best describes your situation?" sub="Be honest — there's no wrong answer here." />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { v: "new", label: "I'm completely new to construction", sub: "Never worked on a site before" },
                { v: "trying", label: "I've been trying but can't land a job", sub: "Applied to places but keep getting knocked back" },
                { v: "some_exp", label: "I have some experience but gaps", sub: "Worked a bit but missing tickets or recent experience" },
                { v: "returning", label: "I'm coming back after time away", sub: "Used to work in the industry, looking to get back in" },
                { v: "switching", label: "I'm switching careers into construction", sub: "Coming from a different industry" },
              ].map(o => (
                <Option key={o.v} selected={answers.situation === o.v} onClick={() => set("situation", o.v)}>
                  <div style={{ fontWeight: 700, color: T.text, fontSize: 15 }}>{o.label}</div>
                  <div style={{ fontSize: 13, color: T.textMed, marginTop: 2 }}>{o.sub}</div>
                </Option>
              ))}
            </div>
          </div>
        );

      case "currentTickets":
        return (
          <div>
            <SectionLabel emoji="🎫" title="Do you have any of these tickets or licences?" sub="Select everything you currently hold — this helps us see where you're at." />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {Object.entries(TICKET_INFO).map(([k, v]) => (
                <Option key={k} multi selected={answers.currentTickets.includes(k)} onClick={() => toggleTicket(k)}>
                  <div style={{ fontWeight: 600, color: T.text, fontSize: 15 }}>{v.label}</div>
                </Option>
              ))}
              <Option multi selected={answers.currentTickets.includes("none")} onClick={() => toggleTicket("none")}>
                <div style={{ fontWeight: 600, color: T.text, fontSize: 15 }}>I don't have any yet</div>
                <div style={{ fontSize: 13, color: T.textMed, marginTop: 2 }}>That's completely okay — most people start here</div>
              </Option>
            </div>
          </div>
        );

      case "interest":
        return (
          <div>
            <SectionLabel emoji="🔨" title="What kind of work are you most interested in?" sub="Pick the area that appeals to you — we'll tailor your pathway." />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { v: "construction", label: "Construction sites", sub: "Building, labouring, hands-on site work" },
                { v: "warehouse", label: "Warehouse & logistics", sub: "Forklift, pick packing, stock management" },
                { v: "traffic", label: "Traffic management", sub: "Road closures, site traffic control" },
                { v: "general", label: "A bit of everything", sub: "I'm open to whatever gets me started" },
                { v: "not_sure", label: "Not sure yet", sub: "I need help figuring that out" },
              ].map(o => (
                <Option key={o.v} selected={answers.interest === o.v} onClick={() => set("interest", o.v)}>
                  <div style={{ fontWeight: 700, color: T.text, fontSize: 15 }}>{o.label}</div>
                  <div style={{ fontSize: 13, color: T.textMed, marginTop: 2 }}>{o.sub}</div>
                </Option>
              ))}
            </div>
          </div>
        );

      case "challenge":
        return (
          <div>
            <SectionLabel emoji="🧱" title="What's been the hardest part?" sub="Select everything that applies — understanding your blockers helps us help you." />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { v: "no_tickets", label: "I don't have the right tickets or licences" },
                { v: "no_experience", label: "Everyone wants experience I don't have" },
                { v: "dont_know_start", label: "I don't know where to even begin" },
                { v: "cost", label: "Training costs are a barrier for me" },
                { v: "no_contacts", label: "I don't know anyone in the industry" },
                { v: "applications", label: "I apply but never hear back" },
              ].map(o => (
                <Option key={o.v} multi selected={answers.challenge.includes(o.v)} onClick={() => toggleChallenge(o.v)}>
                  <div style={{ fontWeight: 600, color: T.text, fontSize: 15 }}>{o.label}</div>
                </Option>
              ))}
            </div>
          </div>
        );

      case "suburb":
        return (
          <div>
            <SectionLabel emoji="📍" title="Where are you based?" sub="So we can point you to relevant opportunities and training near you." />
            <input type="text" placeholder="e.g. Parramatta, Penrith, Liverpool..."
              value={answers.suburb} onChange={e => set("suburb", e.target.value)}
              style={{
                ...inputStyle,
                borderColor: answers.suburb ? T.accent : T.border,
              }}
              onFocus={focusInput} onBlur={blurInput}
            />
          </div>
        );

      case "contact":
        return (
          <div>
            <SectionLabel emoji="📬" title="Where should we send your pathway?" sub="We'll put together a personalised plan and send you helpful next steps." />
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.textMed, marginBottom: 6, display: "block" }}>First Name *</label>
                <input type="text" value={answers.firstName} onChange={e => set("firstName", e.target.value)}
                  placeholder="Your first name" style={inputStyle} onFocus={focusInput} onBlur={blurInput} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.textMed, marginBottom: 6, display: "block" }}>Phone *</label>
                <input type="tel" value={answers.phone} onChange={e => set("phone", e.target.value)}
                  placeholder="04XX XXX XXX" style={inputStyle} onFocus={focusInput} onBlur={blurInput} />
                <p style={{ fontSize: 12, color: T.textLight, margin: "6px 0 0" }}>We'll text you training options — no spam, ever</p>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.textMed, marginBottom: 6, display: "block" }}>Email (optional)</label>
                <input type="email" value={answers.email} onChange={e => set("email", e.target.value)}
                  placeholder="you@email.com" style={inputStyle} onFocus={focusInput} onBlur={blurInput} />
              </div>
            </div>
            {error && (
              <div style={{
                marginTop: 14, padding: "12px 16px", borderRadius: 10,
                background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626",
                fontSize: 14, fontWeight: 600,
              }}>{error}</div>
            )}
          </div>
        );

      case "result":
        return <ResultPage answers={answers} result={result} buildPathway={buildPathway} />;

      default: return null;
    }
  };

  const isIntro = cur === "intro";
  const isResult = cur === "result";
  const isContact = cur === "contact";

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(circle at top, rgba(29,165,90,.12), transparent 36%), ${T.bg}`, fontFamily: "'Outfit', 'Segoe UI', sans-serif", color: T.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(${dir > 0 ? "24" : "-24"}px); } to { opacity: 1; transform: translateX(0); } }
        input::placeholder { color: ${T.textLight}; }
      `}</style>

      <div style={{ maxWidth: 520, margin: "0 auto", padding: "24px 20px" }}>
        {!isIntro && !isResult && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.textLight }}>Step {step} of {STEPS.length - 2}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.accent }}>{pct}%</span>
            </div>
            <div style={{ height: 5, borderRadius: 3, background: T.border, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 3, background: T.accent,
                width: `${pct}%`, transition: "width .4s ease",
              }} />
            </div>
          </div>
        )}

        <div key={animKey} style={isIntro || isResult ? { animation: "fadeUp .4s ease" } : { animation: "slideIn .3s ease" }}>
          {renderStep()}
        </div>

        {!isIntro && !isResult && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28 }}>
            <Btn onClick={() => go(-1)}>← Back</Btn>
            {isContact ? (
              <Btn primary disabled={!canNext() || submitting} onClick={handleSubmit}>
                {submitting ? "Building your plan..." : "See my pathway →"}
              </Btn>
            ) : (
              <Btn primary disabled={!canNext()} onClick={() => go(1)}>Next →</Btn>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   RESULT PAGE — genuine career guidance
   ══════════════════════════════════════════════════════ */
function ResultPage({ answers, result, buildPathway }) {
  const navigate = useNavigate();
  const [communityLinks, setCommunityLinks] = useState({ whatsapp: null, telegram: null });
  const { foundation, core, advanced } = buildPathway();
  const recommended = result?.recommendedTickets || [...foundation, ...core, ...advanced];

  useEffect(() => {
    let cancelled = false;

    const fetchLinks = async () => {
      try {
        const items = await metadataService.getByGroup('LABOURANDO_GROUP_INVITATION');
        if (cancelled) return;

        const map = {};
        items.forEach((item) => {
          map[item.key] = item.value;
        });

        setCommunityLinks({
          whatsapp: map.WHATSAPP_GROUP_INVITE_URL || null,
          telegram: map.TELEGRAM_GROUP_INVITE_URL || null,
        });
      } catch (err) {
        console.warn('Could not load community links:', err.message);
      }
    };

    fetchLinks();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasCostBarrier = answers.challenge.includes("cost");
  const hasExpBarrier = answers.challenge.includes("no_experience");
  const hasContactBarrier = answers.challenge.includes("no_contacts");
  const hasApplicationBarrier = answers.challenge.includes("applications");

  const PhaseCard = ({ phase, emoji, title, color, colorSoft, colorBorder, children, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen || false);
    return (
      <div style={{
        borderRadius: 16, border: `1.5px solid ${colorBorder}`, overflow: "hidden",
        marginBottom: 14, background: T.surface, animation: "fadeUp .5s ease both",
        animationDelay: `${phase * 0.1}s`, boxShadow: T.shadow,
      }}>
        <div onClick={() => setOpen(!open)} style={{
          padding: "18px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14,
          background: open ? colorSoft : T.surface, transition: "background .2s",
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, background: colorSoft,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0,
          }}>{emoji}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: ".5px" }}>Phase {phase}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginTop: 2 }}>{title}</div>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2"
            style={{ transition: "transform .2s", transform: open ? "rotate(180deg)" : "rotate(0)" }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
        {open && <div style={{ padding: "4px 20px 20px" }}>{children}</div>}
      </div>
    );
  };

  const TicketRow = ({ id }) => {
    const t = TICKET_INFO[id];
    if (!t) return null;
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
        borderRadius: 10, background: T.surfaceSoft, marginTop: 8,
        border: `1px solid ${T.border}`,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: T.accentSoft,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0,
        }}>🎫</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: T.text }}>{t.label}</div>
          <div style={{ fontSize: 12, color: T.textMed }}>{t.days} day course · ~${t.price}</div>
        </div>
      </div>
    );
  };

  const Stat = ({ value, label }) => (
    <div style={{ textAlign: "center", flex: 1 }}>
      <div style={{ fontSize: 24, fontWeight: 900, color: T.accent, fontFamily: "'Outfit', sans-serif" }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textMed, marginTop: 2, lineHeight: 1.3 }}>{label}</div>
    </div>
  );

  const Tip = ({ emoji, children }) => (
    <div style={{
      display: "flex", gap: 12, padding: "14px 16px", borderRadius: 10,
      background: T.surfaceWarm, border: `1px solid ${T.borderWarm}`, marginTop: 10,
    }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>{emoji}</span>
      <div style={{ fontSize: 14, color: T.textMed, lineHeight: 1.55 }}>{children}</div>
    </div>
  );

  let phaseNum = 0;

  return (
    <div style={{ animation: "fadeUp .5s ease" }}>
      {/* ── greeting ── */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🛤️</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: T.text, margin: "0 0 10px", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>
          {answers.firstName}, here's your pathway<br />into construction
        </h1>
        <p style={{ fontSize: 16, color: T.textMed, maxWidth: 400, margin: "0 auto", lineHeight: 1.5 }}>
          We've looked at where you're at and mapped out a realistic, step-by-step plan to get you working.
        </p>
      </div>

      {/* ── quick stats bar ── */}
      <div style={{
        display: "flex", gap: 8, padding: "18px 16px", borderRadius: 14,
        background: T.surface, border: `1.5px solid ${T.border}`, marginBottom: 20,
        boxShadow: T.shadow,
      }}>
        <Stat value={`${recommended.length}`} label="tickets to get" />
        <div style={{ width: 1, background: T.border }} />
        <Stat value="1–2 wks" label="to be job-ready" />
        <div style={{ width: 1, background: T.border }} />
        <Stat value="$35-45" label="avg hourly rate" />
      </div>

      {/* ══ PHASE 1 — Foundation ══ */}
      {foundation.length > 0 && (
        <PhaseCard phase={++phaseNum} emoji="🏗️" title="Get your foundation" defaultOpen
          color="#B45309" colorSoft={T.accentSoft} colorBorder={T.accentBorder}>
          <p style={{ fontSize: 14, color: T.textMed, lineHeight: 1.6, margin: "8px 0 0" }}>
            Every construction job in Australia requires a White Card. It's the legal minimum
            to step onto any site — and the good news is it only takes <strong style={{ color: T.text }}>one day</strong> to complete.
          </p>
          {foundation.map(t => <TicketRow key={t} id={t} />)}
          <Tip emoji="💡">
            Many RTOs offer same-week availability. You could have your White Card by Friday and
            be applying for jobs over the weekend.
          </Tip>
        </PhaseCard>
      )}

      {/* ══ PHASE 2 — Start gaining experience ══ */}
      <PhaseCard phase={++phaseNum} emoji="👟" title="Start applying — even before you have everything"
        defaultOpen={foundation.length === 0}
        color="#16A34A" colorSoft={T.greenSoft} colorBorder={T.greenBorder}>
        <p style={{ fontSize: 14, color: T.textMed, lineHeight: 1.6, margin: "8px 0 0" }}>
          <strong style={{ color: T.text }}>Don't wait until you have every ticket.</strong> Many employers
          hire labourers with just a White Card and are willing to train on the job. Getting your foot in
          the door — even in an entry-level role — is the fastest way to build experience and industry contacts.
        </p>

        <div style={{
          marginTop: 14, padding: "16px", borderRadius: 12, background: T.surfaceMint,
          border: `1px solid ${T.greenBorder}`,
        }}>
          <div style={{ fontWeight: 700, color: T.green, fontSize: 14, marginBottom: 10 }}>
              Labourando has roles for every stage
            </div>
            <div style={{ fontSize: 14, color: T.textMed, lineHeight: 1.7 }}>
              Whether you're starting from zero or already have site experience, Labourando has roles for both entry-level and experienced job seekers.
              Sign up, join our Telegram and WhatsApp community, and keep monitoring the jobs so you don't miss new openings.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
              <button onClick={() => navigate('/signup')} style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "12px 18px", borderRadius: 12, border: "none", cursor: "pointer",
                fontWeight: 700, fontSize: 14, fontFamily: "inherit", background: T.accent,
                color: "#071b0e", boxShadow: "0 12px 30px rgba(29,165,90,.22)",
              }}>
                Sign up
              </button>
              {communityLinks.telegram && (
                <a href={communityLinks.telegram} target="_blank" rel="noopener noreferrer" style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  padding: "12px 18px", borderRadius: 12, border: `1px solid ${T.blueBorder}`,
                  color: T.text, textDecoration: "none", fontWeight: 700, fontSize: 14,
                  background: T.surfaceSoft,
                }}>
                  Join Telegram
                </a>
              )}
              {communityLinks.whatsapp && (
                <a href={communityLinks.whatsapp} target="_blank" rel="noopener noreferrer" style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  padding: "12px 18px", borderRadius: 12, border: `1px solid ${T.greenBorder}`,
                  color: T.text, textDecoration: "none", fontWeight: 700, fontSize: 14,
                  background: T.surfaceSoft,
                }}>
                  Join WhatsApp
                </a>
              )}
            </div>
        </div>

        {hasExpBarrier && (
          <Tip emoji="🙌">
            Feeling stuck because everyone wants "experience"? Labour hire agencies are your best friend —
            they specifically place people in entry-level roles and many don't require experience beyond a White Card.
          </Tip>
        )}

        {hasApplicationBarrier && (
          <Tip emoji="📱">
            Not hearing back from applications? Try calling directly. Construction is an industry
            where a phone call or showing up in person goes much further than an online application.
          </Tip>
        )}

        {hasContactBarrier && (
          <Tip emoji="🤝">
            Don't know anyone in the industry? That's normal when starting out. Labour hire agencies
            act as your connection to sites. Once you're on one job, you'll start building a network naturally.
          </Tip>
        )}
      </PhaseCard>

      {/* ══ PHASE 3 — High-value tickets ══ */}
      {core.length > 0 && (
        <PhaseCard phase={++phaseNum} emoji="📈" title="Level up with high-value tickets"
          color="#2563EB" colorSoft={T.blueSoft} colorBorder={T.blueBorder}>
          <p style={{ fontSize: 14, color: T.textMed, lineHeight: 1.6, margin: "8px 0 0" }}>
            Once you've got some work under your belt, these tickets dramatically increase your earning
            potential and the number of roles open to you.
          </p>

          {core.map(t => <TicketRow key={t} id={t} />)}

          <div style={{
            marginTop: 14, padding: "16px", borderRadius: 12,
            background: T.surfaceBlue, border: `1px solid ${T.blueBorder}`,
          }}>
            <div style={{ fontWeight: 700, color: T.blue, fontSize: 14, marginBottom: 12 }}>
              Why these tickets matter — by the numbers:
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {core.includes("heights") && (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 16 }}>📊</span>
                  <div style={{ fontSize: 14, color: T.textMed, lineHeight: 1.5 }}>
                    <strong style={{ color: T.text }}>Working at Heights</strong> — Required for any work above 2m.
                    Opens up scaffolding, roofing, and steel-fixing roles that pay <strong style={{ color: T.text }}>$5-12/hr more</strong> than ground-level labour.
                  </div>
                </div>
              )}
              {core.includes("forklift") && (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 16 }}>📊</span>
                  <div style={{ fontSize: 14, color: T.textMed, lineHeight: 1.5 }}>
                    <strong style={{ color: T.text }}>Forklift Licence</strong> — One of the most in-demand tickets in Australia.
                    Forklift operators average <strong style={{ color: T.text }}>$30-40/hr</strong> with consistent work availability across construction and warehousing.
                  </div>
                </div>
              )}
            </div>
          </div>

          {hasCostBarrier && (
            <Tip emoji="💸">
              Worried about cost? Many people use their first few weeks of pay to fund their next ticket.
              At $35+/hr, a $350–$450 ticket pays for itself in a single day on site. Some employers will
              even cover training costs for good workers they want to keep.
            </Tip>
          )}
        </PhaseCard>
      )}

      {/* ══ PHASE 4 — Specialise ══ */}
      {advanced.length > 0 && (
        <PhaseCard phase={++phaseNum} emoji="🚀" title="Specialise and earn more"
          color="#7C3AED" colorSoft="rgba(124,58,237,.06)" colorBorder="rgba(124,58,237,.18)">
          <p style={{ fontSize: 14, color: T.textMed, lineHeight: 1.6, margin: "8px 0 0" }}>
            These specialised tickets open doors to higher-paying, more skilled roles.
            Most people work toward these after a few months of experience.
          </p>

          {advanced.map(t => <TicketRow key={t} id={t} />)}

          <div style={{
            marginTop: 14, padding: "16px", borderRadius: 12,
            background: "rgba(124,58,237,.04)", border: "1px solid rgba(124,58,237,.12)",
          }}>
            <div style={{ fontWeight: 700, color: "#7C3AED", fontSize: 14, marginBottom: 8 }}>
              The long game pays off:
            </div>
            <div style={{ fontSize: 14, color: T.textMed, lineHeight: 1.6 }}>
              Workers with 3+ tickets earn on average <strong style={{ color: T.text }}>35% more</strong> than
              those with just a White Card. More tickets also means more job security — you become harder
              to replace and first in line when new projects start.
            </div>
          </div>
        </PhaseCard>
      )}

      {/* ══ Encouragement ══ */}
      <div style={{
        borderRadius: 16, padding: "24px 20px", marginBottom: 14, marginTop: 10,
        background: T.surfaceWarm, border: `1.5px solid ${T.borderWarm}`,
        animation: "fadeUp .5s ease both", animationDelay: ".4s", boxShadow: T.shadow,
      }}>
        <div style={{ fontSize: 24, marginBottom: 10 }}>💛</div>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: "0 0 10px", fontFamily: "'Outfit', sans-serif" }}>
          One last thing, {answers.firstName}
        </h3>
        <p style={{ fontSize: 14, color: T.textMed, lineHeight: 1.7, margin: 0 }}>
          Everyone who works in construction started exactly where you are. The people running sites today
          were once labourers figuring out their first week. The key is to <strong style={{ color: T.text }}>start —
          even if it's imperfect</strong>. Get your first ticket, take the first job that comes your way, and
          build from there. You'll be surprised how quickly things move once you take that first step.
        </p>
      </div>

      {/* ── CTA ── */}
      <div style={{
        textAlign: "center", padding: "24px 0", marginTop: 8,
        animation: "fadeUp .5s ease both", animationDelay: ".5s",
      }}>
        <p style={{ fontSize: 15, color: T.textMed, marginBottom: 16, lineHeight: 1.5 }}>
          Want to save your pathway and keep moving?<br />
          Create your Labourando account to continue into the platform and get next steps tailored to you.
        </p>
        <button onClick={() => navigate('/signup')} style={{
          display: "inline-flex", alignItems: "center", gap: 10, padding: "16px 32px",
          borderRadius: 14, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 16,
          fontFamily: "inherit", background: `linear-gradient(135deg, ${T.accent}, #179c4b)`, color: "#071b0e",
          boxShadow: "0 12px 30px rgba(29,165,90,.22)", transition: "all .2s",
        }}>
          Create your Labourando account →
        </button>
        <p style={{ fontSize: 12, color: T.textLight, marginTop: 12 }}>
          Free, no pressure — just a faster way to get started
        </p>
      </div>
    </div>
  );
}